import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Spinner, Alert, Dropdown, Badge } from 'react-bootstrap';
import { FaSave, FaFileExport, FaExclamationTriangle, FaTimes, FaUndo, FaRedo, FaSyncAlt, FaUsers } from 'react-icons/fa';
import styled from 'styled-components';
import { useI18n } from '../../i18n';
import { cloudAPI } from '../../utils/httpClient';
import CollaboratorList from './CollaboratorList';

// Univer imports
import { createUniver, LocaleType, merge } from '@univerjs/presets';
import { UniverSheetsCorePreset } from '@univerjs/preset-sheets-core';
import '@univerjs/preset-sheets-core/lib/index.css';
import sheetsEnUS from '@univerjs/preset-sheets-core/locales/en-US';
import sheetsZhCN from '@univerjs/preset-sheets-core/locales/zh-CN';

// Styled components
const EditorContainer = styled.div`
  height: 70vh;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ToolbarSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  position: relative;
  z-index: 1000;
`;

const ToolbarButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SaveStatus = styled.span`
  font-size: 0.85rem;
  color: ${props => props.$unsaved ? '#dc3545' : '#6c757d'};
`;

const UniverContainer = styled.div`
  flex: 1;
  width: 100%;
  overflow: hidden;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const VectorizationWarningBanner = styled(Alert)`
  margin: 0;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// Circular progress indicator positioned at table's bottom-right corner
const CircularProgressContainer = styled.div`
  position: absolute;
  bottom: 24px;
  right: 24px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(128, 128, 128, 0.85);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  min-width: 140px;
`;

const CircularProgress = styled.div`
  position: relative;
  width: 80px;
  height: 80px;

  svg {
    animation: spin 1.5s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .spinner-track {
    fill: none;
    stroke: rgba(255, 255, 255, 0.3);
    stroke-width: 6;
  }

  .spinner-arc {
    fill: none;
    stroke: #ffffff;
    stroke-width: 6;
    stroke-linecap: round;
    stroke-dasharray: 180;
    stroke-dashoffset: 90;
  }

  .progress-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
  }
`;

const ProgressLabel = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #ffffff;
  text-align: center;
  max-width: 120px;
`;

const SpreadsheetEditorModal = ({
  show,
  onHide,
  fileItem,
  onSaveSuccess,
  readOnly = false
}) => {
  const { t, lang } = useI18n();
  const univerRef = useRef(null);
  const containerRef = useRef(null);
  const workbookRef = useRef(null);
  const isInitializingRef = useRef(true);  // Flag to ignore mutations during initial load

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [showVectorizationWarning, setShowVectorizationWarning] = useState(false);
  const [acknowledgedWarning, setAcknowledgedWarning] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Update vectorization state
  const [updatingVectorization, setUpdatingVectorization] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateProgressMessage, setUpdateProgressMessage] = useState('');
  const [pendingChanges, setPendingChanges] = useState(null);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

  // Collaboration state
  const [collaborationEnabled, setCollaborationEnabled] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const presenceIntervalRef = useRef(null);

  // Determine if file is an organization file (collaboration eligible)
  const isOrgFile = !!fileItem?.org_code;

  // Determine if file is vectorized and would be affected by changes
  const isVectorized = !!fileItem?.vectorized;
  // Track vectorization stale state locally (updated after save)
  const [localVectorizationStale, setLocalVectorizationStale] = useState(fileItem?.vectorization_stale || false);

  // Update local state when fileItem prop changes
  useEffect(() => {
    setLocalVectorizationStale(fileItem?.vectorization_stale || false);
  }, [fileItem?.vectorization_stale]);

  // Use local state for UI decisions
  const isVectorizationStale = localVectorizationStale;

  // Single variable to determine if vectorization update can be performed
  // True when: file is vectorized, vectorization is stale, no update in progress, no unsaved changes
  const canUpdateVectorization = isVectorized && isVectorizationStale && !updatingVectorization && !hasUnsavedChanges;

  // Initialize Univer when modal opens
  useEffect(() => {
    if (!show || !containerRef.current || !fileItem) return;

    let univerInstance = null;

    const initUniver = async () => {
      setLoading(true);
      setError(null);
      isInitializingRef.current = true;  // Mark as initializing to ignore mutations

      try {
        // Load file content from backend
        const response = await cloudAPI.getSpreadsheetContent(fileItem.id);
        const fileData = response.data;

        // Create Univer instance
        const { univer, univerAPI } = createUniver({
          locale: lang === 'zh' ? LocaleType.ZH_CN : LocaleType.EN_US,
          locales: {
            [LocaleType.EN_US]: sheetsEnUS,
            [LocaleType.ZH_CN]: sheetsZhCN,
          },
          presets: [
            UniverSheetsCorePreset({
              container: containerRef.current,
            }),
          ],
        });

        univerInstance = univer;
        univerRef.current = univerAPI;

        // Create workbook with data
        const workbook = univerAPI.createWorkbook(fileData.workbookData || {
          id: fileItem.id,
          name: fileItem.name,
          sheets: {
            sheet1: {
              name: 'Sheet1',
              cellData: fileData.cellData || {},
              rowCount: fileData.rowCount || 1000,
              columnCount: fileData.columnCount || 26,
            }
          }
        });

        workbookRef.current = workbook;

        // Set up change listener - but ignore mutations during initial load
        if (!readOnly) {
          univerAPI.onCommandExecuted((command) => {
            // Only track changes after initialization is complete
            // (Univer triggers mutations during workbook creation which aren't real user changes)
            if (command.id.includes('mutation') && !isInitializingRef.current) {
              setHasUnsavedChanges(true);
            }
          });
        }

        // Show vectorization warning if file is vectorized and not yet acknowledged
        if (isVectorized && !isVectorizationStale && !readOnly && !acknowledgedWarning) {
          setShowVectorizationWarning(true);
        }

        // Mark initialization as complete after a longer delay
        // Univer may trigger multiple rounds of internal mutations during setup
        // 500ms should be sufficient for most cases
        setTimeout(() => {
          isInitializingRef.current = false;
          // Ensure hasUnsavedChanges is false after initialization
          setHasUnsavedChanges(false);
        }, 500);

        setLoading(false);
      } catch (err) {
        console.error('Failed to initialize spreadsheet:', err);
        setError(t('spreadsheet:errors.loadFailed'));
        setLoading(false);
      }
    };

    initUniver();

    // Cleanup on unmount
    return () => {
      if (univerInstance) {
        univerInstance.dispose();
      }
      univerRef.current = null;
      workbookRef.current = null;
      // Reset initialization flag for next open
      isInitializingRef.current = true;
    };
  }, [show, fileItem?.id, lang, readOnly, acknowledgedWarning, isVectorized, isVectorizationStale, t]);

  // Collaboration session management
  useEffect(() => {
    if (!show || !fileItem?.id || !isOrgFile || readOnly) {
      return;
    }

    let mounted = true;

    const initCollaboration = async () => {
      try {
        // Try to join collaboration session
        const result = await cloudAPI.createCollaborationSession(fileItem.id);

        if (mounted && result.success) {
          setCollaborationEnabled(true);
          setCollaborators(result.active_users || []);
          setCurrentUserId(result.collaborator?.user_id);

          // Start presence heartbeat (every 30 seconds)
          presenceIntervalRef.current = setInterval(async () => {
            try {
              const presenceResult = await cloudAPI.updatePresence(fileItem.id);
              if (mounted && presenceResult.active_users) {
                setCollaborators(presenceResult.active_users);
              }
            } catch (err) {
              console.error('Failed to update presence:', err);
            }
          }, 30000);
        }
      } catch (err) {
        // Collaboration not available - might be disabled or permission issue
        console.log('Collaboration not available:', err.message);
        setCollaborationEnabled(false);
      }
    };

    initCollaboration();

    // Cleanup: leave session when modal closes
    return () => {
      mounted = false;
      if (presenceIntervalRef.current) {
        clearInterval(presenceIntervalRef.current);
        presenceIntervalRef.current = null;
      }
      // Leave session
      if (fileItem?.id) {
        cloudAPI.leaveCollaborationSession(fileItem.id).catch(() => {});
      }
      setCollaborationEnabled(false);
      setCollaborators([]);
    };
  }, [show, fileItem?.id, isOrgFile, readOnly]);

  // Handle save - block during vectorization
  const handleSave = useCallback(async () => {
    if (!univerRef.current || !workbookRef.current || readOnly) return;

    // Block saving during vectorization update
    if (updatingVectorization) {
      setError(t('spreadsheet:vectorization.cannotSaveDuringUpdate'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Force commit any active cell editing before saving
      // This ensures cells being edited are saved with their current content
      if (containerRef.current) {
        // Method 1: Blur focus to commit cell edit
        containerRef.current.blur();
        // Method 2: Try to end editing via Univer command
        try {
          univerRef.current.executeCommand('sheet.operation.set-cell-edit-visible', { visible: false });
        } catch (e) {
          // Command may not exist in all Univer versions, ignore error
        }
        // Small delay to ensure edit is committed
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // Get workbook data from Univer
      const workbook = univerRef.current.getActiveWorkbook();
      const snapshot = workbook.getSnapshot();

      // Send to backend
      const result = await cloudAPI.saveSpreadsheet(fileItem.id, {
        workbookData: snapshot,
        fileName: fileItem.name
      });

      setHasUnsavedChanges(false);
      setLastSavedTime(new Date());

      // If file is vectorized and content changed, mark vectorization as stale locally
      if (isVectorized && result?.content_changed) {
        setLocalVectorizationStale(true);
      }

      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (err) {
      console.error('Failed to save spreadsheet:', err);
      setError(t('spreadsheet:errors.saveFailed', { error: err.message }));
    } finally {
      setSaving(false);
    }
  }, [fileItem, readOnly, onSaveSuccess, t, updatingVectorization, isVectorized]);

  // Handle export
  const handleExport = useCallback(async (format) => {
    if (!univerRef.current) return;

    try {
      const workbook = univerRef.current.getActiveWorkbook();
      const snapshot = workbook.getSnapshot();

      const response = await cloudAPI.exportSpreadsheet(fileItem.id, {
        format,
        workbookData: snapshot
      });

      // Download the file
      const blob = new Blob([response.data], {
        type: format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileItem.name.replace(/\.[^/.]+$/, '')}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export spreadsheet:', err);
      setError(t('spreadsheet:errors.exportFailed', { error: err.message }));
    }
  }, [fileItem, t]);

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCloseConfirm(true);
    } else {
      onHide();
    }
  }, [hasUnsavedChanges, onHide]);

  // Handle close without saving
  const handleCloseWithoutSave = useCallback(() => {
    setShowCloseConfirm(false);
    setHasUnsavedChanges(false);
    onHide();
  }, [onHide]);

  // Handle save and close
  const handleSaveAndClose = useCallback(async () => {
    await handleSave();
    setShowCloseConfirm(false);
    onHide();
  }, [handleSave, onHide]);

  // Handle undo/redo
  const handleUndo = useCallback(() => {
    if (univerRef.current) {
      univerRef.current.undo();
    }
  }, []);

  const handleRedo = useCallback(() => {
    if (univerRef.current) {
      univerRef.current.redo();
    }
  }, []);

  // Format saved time
  const formatSavedTime = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString(lang === 'zh' ? 'zh-CN' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch pending changes count before update
  const fetchPendingChanges = useCallback(async () => {
    if (!fileItem?.id) return null;
    try {
      const response = await cloudAPI.getVectorizationChanges(fileItem.id);
      setPendingChanges(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch pending changes:', err);
      return null;
    }
  }, [fileItem?.id]);

  // Handle update vectorization button click - show alert if already in progress
  const handleUpdateVectorizationClick = useCallback(() => {
    // Block if vectorization is already in progress - show alert instead of disable
    if (updatingVectorization) {
      setError(t('spreadsheet:vectorization.cannotUpdateDuringUpdate'));
      return;
    }

    // If we have unsaved changes, require saving first
    if (hasUnsavedChanges) {
      setError(t('spreadsheet:vectorization.saveFirst'));
      return;
    }

    // Show confirmation dialog immediately
    setShowUpdateConfirm(true);
    // Fetch pending changes in background for display (non-blocking)
    fetchPendingChanges();
  }, [hasUnsavedChanges, fetchPendingChanges, t, updatingVectorization]);

  // Start incremental vectorization
  const startIncrementalVectorization = useCallback(async () => {
    setShowUpdateConfirm(false);
    setUpdatingVectorization(true);
    setUpdateProgress(0);
    setUpdateProgressMessage(t('spreadsheet:vectorization.progressSteps.starting'));
    setError(null);

    try {
      // Start the task
      const result = await cloudAPI.startIncrementalVectorization(fileItem.id);
      const taskId = result.task_id;

      // Poll for progress
      const pollInterval = setInterval(async () => {
        try {
          // Note: cloudAPI.getVectorizationProgress returns response.data = { success, data: { state, current, total, meta } }
          const progressResponse = await cloudAPI.getVectorizationProgress(taskId);

          // Extract nested data - backend returns { success: true, data: { state, current, total, meta: { description } } }
          const data = progressResponse.data || progressResponse;
          const status = data.state || data.status || 'PENDING';
          const current = data.current ?? 0;
          const total = data.total ?? 100;
          const progress = total > 0 ? Math.round((current / total) * 100) : 0;
          const message = data.meta?.description || data.message || '';

          setUpdateProgress(progress);
          if (message) {
            setUpdateProgressMessage(message);
          }

          if (status === 'SUCCESS') {
            clearInterval(pollInterval);
            setUpdatingVectorization(false);
            setPendingChanges(null);
            setUpdateProgressMessage('');
            // Mark vectorization as up-to-date locally
            setLocalVectorizationStale(false);
            // Notify parent to refresh file data
            if (onSaveSuccess) {
              onSaveSuccess();
            }
          } else if (status === 'FAILURE') {
            clearInterval(pollInterval);
            setUpdatingVectorization(false);
            setUpdateProgressMessage('');
            const errorMsg = data.result || data.error || message || 'Unknown error';
            setError(t('spreadsheet:vectorization.updateFailed', { error: errorMsg }));
          }
        } catch (err) {
          console.error('Failed to fetch progress:', err);
        }
      }, 1500);

      // Cleanup on unmount
      return () => clearInterval(pollInterval);
    } catch (err) {
      console.error('Failed to start incremental vectorization:', err);
      setUpdatingVectorization(false);
      setUpdateProgressMessage('');
      setError(t('spreadsheet:vectorization.updateFailed', { error: err.message }));
    }
  }, [fileItem?.id, onSaveSuccess, t]);

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        fullscreen="lg-down"
        backdrop="static"
        keyboard={false}
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('spreadsheet:editor.title')} - {fileItem?.name}
            {readOnly && (
              <Badge bg="secondary" className="ms-2">
                {t('spreadsheet:editor.readOnly')}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0">
          <EditorContainer>
            {/* Vectorization Warning Banner */}
            {showVectorizationWarning && !acknowledgedWarning && (
              <VectorizationWarningBanner variant="warning" onClose={() => {
                setShowVectorizationWarning(false);
                setAcknowledgedWarning(true);
              }} dismissible>
                <div>
                  <FaExclamationTriangle className="me-2" />
                  <strong>{t('spreadsheet:warnings.vectorizationWarning')}</strong>
                  <span className="ms-2">{t('spreadsheet:warnings.editWillOutdateVectorization')}</span>
                </div>
              </VectorizationWarningBanner>
            )}

            {/* Stale vectorization indicator - only banner, no duplicate toolbar button */}
            {isVectorizationStale && (
              <VectorizationWarningBanner variant="info">
                <div className="d-flex align-items-center">
                  <FaExclamationTriangle className="me-2" />
                  <div>
                    <strong>{t('spreadsheet:vectorization.outdated')}</strong>
                    <span className="ms-2">{t('spreadsheet:vectorization.needsUpdate')}</span>
                  </div>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleUpdateVectorizationClick}
                  disabled={!canUpdateVectorization}
                >
                  {updatingVectorization ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-1" />
                      {t('spreadsheet:vectorization.updating')}
                    </>
                  ) : (
                    <>
                      <FaSyncAlt className="me-1" />
                      {t('spreadsheet:vectorization.updateButton')}
                    </>
                  )}
                </Button>
              </VectorizationWarningBanner>
            )}

            {/* Toolbar */}
            {!readOnly && (
              <ToolbarSection>
                <ToolbarButtons>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSave}
                    disabled={saving || !hasUnsavedChanges}
                  >
                    {saving ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-1" />
                        {t('spreadsheet:editor.toolbar.saving')}
                      </>
                    ) : (
                      <>
                        <FaSave className="me-1" />
                        {t('spreadsheet:editor.toolbar.save')}
                      </>
                    )}
                  </Button>

                  <Button variant="outline-secondary" size="sm" onClick={handleUndo}>
                    <FaUndo className="me-1" />
                    {t('spreadsheet:editor.toolbar.undo')}
                  </Button>

                  <Button variant="outline-secondary" size="sm" onClick={handleRedo}>
                    <FaRedo className="me-1" />
                    {t('spreadsheet:editor.toolbar.redo')}
                  </Button>

                  <Dropdown
                    autoClose="outside"
                    onToggle={(isOpen, event, metadata) => {
                      // Prevent Univer from stealing focus
                      if (isOpen && event) {
                        event.stopPropagation?.();
                      }
                    }}
                  >
                    <Dropdown.Toggle variant="outline-secondary" size="sm">
                      <FaFileExport className="me-1" />
                      {t('spreadsheet:editor.toolbar.export')}
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      renderOnMount
                      popperConfig={{ strategy: 'fixed' }}
                      style={{ zIndex: 9999 }}
                    >
                      <Dropdown.Item onClick={() => handleExport('xlsx')}>
                        {t('spreadsheet:editor.toolbar.exportXlsx')}
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleExport('csv')}>
                        {t('spreadsheet:editor.toolbar.exportCsv')}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                </ToolbarButtons>

                <StatusSection>
                  {/* Collaboration status */}
                  {collaborationEnabled && collaborators.length > 0 && (
                    <CollaboratorList
                      collaborators={collaborators}
                      currentUserId={currentUserId}
                      maxDisplay={3}
                    />
                  )}

                  {/* Organization file indicator (for non-collaborative) */}
                  {isOrgFile && !collaborationEnabled && (
                    <Badge bg="secondary" title={t('spreadsheet:collaboration.orgFileOnly')}>
                      <FaUsers className="me-1" />
                      {t('cloud:cloudSpace.labels.orgFile')}
                    </Badge>
                  )}

                  {hasUnsavedChanges ? (
                    <SaveStatus $unsaved>
                      {t('spreadsheet:editor.unsaved')}
                    </SaveStatus>
                  ) : lastSavedTime ? (
                    <SaveStatus>
                      {t('spreadsheet:editor.saved', { time: formatSavedTime(lastSavedTime) })}
                    </SaveStatus>
                  ) : null}

                  {isVectorized && (
                    <Badge bg={isVectorizationStale ? 'warning' : 'success'}>
                      {isVectorizationStale
                        ? t('spreadsheet:vectorization.outdated')
                        : t('spreadsheet:vectorization.upToDate')}
                    </Badge>
                  )}
                </StatusSection>
              </ToolbarSection>
            )}

            {/* Error display */}
            {error && (
              <Alert variant="danger" className="m-2" onClose={() => setError(null)} dismissible>
                {error}
              </Alert>
            )}

            {/* Univer container */}
            <UniverContainer ref={containerRef} />

            {/* Loading overlay */}
            {loading && (
              <LoadingOverlay>
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">{t('spreadsheet:editor.loading')}</p>
              </LoadingOverlay>
            )}

            {/* Circular progress indicator for vectorization - positioned at table's bottom-right */}
            {updatingVectorization && (
              <CircularProgressContainer>
                <CircularProgress>
                  <svg width="80" height="80" viewBox="0 0 80 80">
                    <circle className="spinner-track" cx="40" cy="40" r="36" />
                    <circle className="spinner-arc" cx="40" cy="40" r="36" />
                  </svg>
                  <span className="progress-text">{updateProgress}%</span>
                </CircularProgress>
                <ProgressLabel>
                  {updateProgressMessage || t('spreadsheet:vectorization.updating')}
                </ProgressLabel>
              </CircularProgressContainer>
            )}
          </EditorContainer>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            <FaTimes className="me-1" />
            {t('spreadsheet:actions.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm close with unsaved changes */}
      <Modal show={showCloseConfirm} onHide={() => setShowCloseConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('spreadsheet:warnings.unsavedChangesTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('spreadsheet:warnings.unsavedChanges')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseWithoutSave}>
            {t('spreadsheet:warnings.closeWithoutSave')}
          </Button>
          <Button variant="primary" onClick={handleSaveAndClose}>
            {t('spreadsheet:warnings.saveAndClose')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm update vectorization */}
      <Modal show={showUpdateConfirm} onHide={() => setShowUpdateConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('spreadsheet:vectorization.confirmUpdateTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('spreadsheet:vectorization.confirmUpdateMessage')}</p>
          {pendingChanges && (
            <div className="mt-3">
              <p className="mb-2"><strong>{t('spreadsheet:vectorization.pendingChanges')}:</strong></p>
              <ul className="list-unstyled ms-3">
                {pendingChanges.added > 0 && (
                  <li className="text-success">
                    + {t('spreadsheet:vectorization.changes.added', { count: pendingChanges.added })}
                  </li>
                )}
                {pendingChanges.modified > 0 && (
                  <li className="text-warning">
                    ~ {t('spreadsheet:vectorization.changes.modified', { count: pendingChanges.modified })}
                  </li>
                )}
                {pendingChanges.deleted > 0 && (
                  <li className="text-danger">
                    - {t('spreadsheet:vectorization.changes.deleted', { count: pendingChanges.deleted })}
                  </li>
                )}
                {pendingChanges.unchanged > 0 && (
                  <li className="text-muted">
                    = {t('spreadsheet:vectorization.changes.unchanged', { count: pendingChanges.unchanged })}
                  </li>
                )}
              </ul>
              <p className="text-muted mt-2">
                <small>{t('spreadsheet:vectorization.incrementalNote')}</small>
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateConfirm(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="primary" onClick={startIncrementalVectorization}>
            <FaSyncAlt className="me-1" />
            {t('spreadsheet:vectorization.startUpdate')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SpreadsheetEditorModal;
